import { useFormAPI } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import { placeDefaultAppliedPubSub } from "@/features/form-fields/basic-fields/placeDefaultAppliedPubSub";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { usePageContextQuery } from "@/shared/page-context";
import { useSubscribe } from "@/shared/utils/useSubscribe";

interface Params {
    matchAllTermsExactly: boolean;
    matchAllTermsSupported: boolean;
}

/**
 * Enforces the MATE invariant on location exactness after a place selection.
 *
 * When a place is selected while MATE is active (test arm only), the location exactness must
 * end up as ThisPlace. PlaceExactness (features layer) independently applies the computed
 * level-based default in response to the selection, after an async place-data fetch.
 *
 * Rather than guessing when that async write lands (its timing depends on a fetch of unknown
 * duration), this reconciler waits for PlaceExactness's explicit completion signal
 * (`placeDefaultAppliedPubSub`), which fires immediately after the default is written. It
 * then overrides to ThisPlace. This keeps the reconciler agnostic to *how* PlaceExactness
 * computes or times its write — it only reacts to the fact that the write has happened.
 *
 * EGE-9399
 */
export const useOnPlaceSelection = ({ matchAllTermsExactly, matchAllTermsSupported }: Params) => {
    const { getFieldValue, setFieldValue } = useFormAPI();
    const { data: pageContext } = usePageContextQuery();
    const { defaultPlaceProximityEnabled } = pageContext.runtimeConfig;

    useSubscribe(placeDefaultAppliedPubSub, (fieldPath, appliedGpid) => {
        // Only relevant in the test arm — control arm already sets ThisPlace in PlaceExactness.
        if (!defaultPlaceProximityEnabled) return;

        // Only act when MATE is supported and currently on.
        if (!matchAllTermsSupported || !matchAllTermsExactly) return;

        // Stale-write guard: the completion signal is for the selection that produced
        // `appliedGpid`. If the field's gpid has moved on (place swapped or cleared) since,
        // this override no longer applies. Form state may hold a raw number (autocomplete
        // item Id) while the channel always carries a string, so compare as strings.
        const gpidPath = `${fieldPath}.gpid` as PrefixObjectAccessor<FormValues, []>;
        const currentGpid = getFieldValue(gpidPath) as string | number | null | undefined;
        if (String(currentGpid ?? "") !== appliedGpid) return;

        const exactnessPath = `${fieldPath}.locationExactness` as PrefixObjectAccessor<FormValues, []>;
        setFieldValue(exactnessPath, LOCATION_EXACTNESS_SCOPE.ThisPlace);
    });
};
